import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { KeyRound, User, Plane, ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { validateToken } from '../../lib/flightSyncApi';

export default function OnboardingPage({ onDone }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [token, setToken] = useState(searchParams.get('token') || '');
  const [pilotName, setPilotName] = useState('');
  const [droneModel, setDroneModel] = useState('');
  const [droneSerial, setDroneSerial] = useState('');
  const [anac, setAnac] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [validated, setValidated] = useState(null);

  const doValidate = async (t = null) => {
    const candidate = (t || token || '').trim();
    if (!candidate) return toast.error('Cole o token do projeto');
    setLoading(true);
    try {
      const info = await validateToken(candidate);
      setValidated(info);
      toast.success(`Token valido - Projeto: ${info.project_name}`);
      setStep(2);
      // Remove token from URL so it's not shared accidentally
      if (searchParams.has('token')) {
        searchParams.delete('token');
        setSearchParams(searchParams, { replace: true });
      }
    } catch (e) {
      toast.error(e.message || 'Token invalido');
    } finally {
      setLoading(false);
    }
  };

  // Auto-validate when token comes via URL (?token=XXXX from QR code)
  useEffect(() => {
    const urlToken = searchParams.get('token');
    if (urlToken && !validated && step === 1) {
      doValidate(urlToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finish = () => {
    if (!pilotName.trim()) return toast.error('Informe seu nome');
    if (!droneModel.trim()) return toast.error('Informe o modelo do drone');
    const ctx = {
      token: token.trim(),
      project_id: validated.project_id,
      project_name: validated.project_name,
      token_name: validated.token_name,
      pilot_name: pilotName.trim(),
      drone_model: droneModel.trim(),
      drone_serial: droneSerial.trim(),
      anac: anac.trim(),
      created_at: new Date().toISOString(),
    };
    onDone(ctx);
  };

  return (
    <div data-testid="fs-onboarding">
      <div className="fs-hero">
        <div className="fs-hero-label">BEM-VINDO</div>
        <div className="fs-hero-value">FlightSync</div>
        <div className="fs-hero-sub">
          Extraia, armazene e sincronize seus logs de voo DJI automaticamente
          com gestaoderpas.com.br
        </div>
      </div>

      {step === 1 && (
        <>
          <div className="fs-card" data-testid="fs-step-token">
            <div className="fs-card-title"><KeyRound size={12} /> 1 / 2 · TOKEN DO PROJETO</div>
            <p className="fs-card-sub">
              Solicite o token ao administrador do projeto em gestaoderpas.com.br → Dados de Voo → Tokens.
            </p>
            <label className="fs-label">Token de sincronizacao</label>
            <input
              className="fs-input"
              placeholder="ABC2F9H4"
              value={token}
              onChange={(e) => setToken(e.target.value.trim().toUpperCase())}
              autoCapitalize="characters"
              spellCheck="false"
              autoCorrect="off"
              maxLength={32}
              style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.15em', fontSize: 18, textAlign: 'center' }}
              data-testid="fs-input-token"
            />
            <p style={{ fontSize: 11, color: 'var(--fs-text-dim)', margin: '6px 0 0 0' }}>
              Novos tokens tem 8 caracteres (ex: ABC2F9H4). Tokens antigos tambem funcionam.
            </p>
            <div style={{ marginTop: 14 }}>
              <button className="fs-btn fs-btn-primary" onClick={() => doValidate()} disabled={loading || !token.trim()} style={{ width: '100%' }} data-testid="fs-btn-validate">
                {loading ? <Loader2 size={16} className="fs-spin" /> : <ShieldCheck size={16} />}
                VALIDAR TOKEN
              </button>
            </div>
          </div>

          <div className="fs-card">
            <div className="fs-card-title">PRIVACIDADE</div>
            <p style={{ fontSize: 13, color: 'var(--fs-text-dim)', lineHeight: 1.6, margin: 0 }}>
              Os dados de voo (GPS, altitude, bateria, velocidade) sao enviados unicamente para o projeto
              vinculado ao token. Voce pode operar offline — os voos ficam em fila local e sao sincronizados
              quando a conexao retornar.
            </p>
          </div>
        </>
      )}

      {step === 2 && validated && (
        <>
          <div className="fs-card" data-testid="fs-step-profile">
            <div className="fs-card-title"><User size={12} /> 2 / 2 · SEU PERFIL</div>
            <p className="fs-card-sub">
              Projeto: <span style={{ color: 'var(--fs-cyan)' }}>{validated.project_name}</span>
            </p>

            <label className="fs-label">Nome do piloto</label>
            <input className="fs-input" value={pilotName} onChange={(e) => setPilotName(e.target.value)} placeholder="Ex: Joao da Silva" data-testid="fs-input-pilot-name" />

            <div style={{ height: 12 }} />
            <label className="fs-label">Modelo do drone</label>
            <input className="fs-input" value={droneModel} onChange={(e) => setDroneModel(e.target.value)} placeholder="Ex: DJI Mavic 3" data-testid="fs-input-drone-model" />

            <div style={{ height: 12 }} />
            <label className="fs-label">Numero serial (opcional)</label>
            <input className="fs-input" value={droneSerial} onChange={(e) => setDroneSerial(e.target.value)} placeholder="Ex: 1581F6Q..." data-testid="fs-input-drone-serial" />

            <div style={{ height: 12 }} />
            <label className="fs-label">CANAC / ANAC (opcional)</label>
            <input className="fs-input" value={anac} onChange={(e) => setAnac(e.target.value)} placeholder="Licenca ANAC" data-testid="fs-input-anac" />

            <div style={{ height: 18 }} />
            <button className="fs-btn fs-btn-primary" onClick={finish} style={{ width: '100%' }} data-testid="fs-btn-finish-onboarding">
              <Plane size={16} /> COMECAR A SINCRONIZAR
            </button>
            <button className="fs-btn fs-btn-ghost" onClick={() => setStep(1)} style={{ width: '100%', marginTop: 8 }}>
              VOLTAR
            </button>
          </div>
        </>
      )}
    </div>
  );
}
